import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import Header from "../../components/Header/Header";
import TitleBand from "../../components/TitleBand/TitleBand";
import ChatWidget from "../../components/ChatWidget/ChatWidget";
import { PageSeo } from "../../seo/Seo";
import "./Legal.css";

export type LegalSection = { id: string; title: string; content: React.ReactNode };

type LegalLayoutProps = {
  seo: { page: string; path: string };
  title: string;
  icon: LucideIcon;
  crumbs?: { label: string; to: string }[];
  intro?: React.ReactNode;
  sections: LegalSection[];
  updated?: string;
};

const pad = (n: number) => String(n + 1).padStart(2, "0");
// Os títulos das traduções já vêm numerados ("1. Aceitação dos Termos"): o número sai do texto e é desenhado à parte.
const stripNumber = (title: string) => title.replace(/^\d+\.\s*/, "");

// Páginas legais (Termos de Uso, Política de Privacidade, LGPD e Regulamento da Universidade Calriz): faixa do título,
// sumário fixo à esquerda com a seção visível em destaque e o texto numa coluna de leitura.
export default function LegalLayout({ seo, title, icon, crumbs, intro, sections, updated }: LegalLayoutProps) {
  const { t } = useTranslation();
  const [active, setActive] = useState(sections[0]?.id);
  const ids = sections.map((s) => s.id).join(",");

  // seção que passa pelo meio da tela vira a ativa no sumário
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => entry.isIntersecting && setActive(entry.target.id)),
      { rootMargin: "-45% 0px -50% 0px" }
    );
    ids.split(",").forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [ids]);

  return (
    <div className="cz-page cz-scope cz-legal-page">
      <PageSeo page={seo.page} path={seo.path} />
      <Header />
      <main>
        <TitleBand title={title} icon={icon} crumbs={crumbs} />

        <div className="cz-legal">
          <nav className="cz-legal__index" aria-label={t("legal.on_this_page", "Nesta página")}>
            <span className="cz-eyebrow">{t("legal.on_this_page", "Nesta página")}</span>
            <ol>
              {sections.map(({ id, title: sectionTitle }, i) => (
                <li key={id}>
                  <Link
                    to={`#${id}`}
                    className={active === id ? "is-active" : undefined}
                    aria-current={active === id ? "location" : undefined}
                  >
                    <span className="cz-legal__index-num">{pad(i)}</span>
                    {stripNumber(sectionTitle)}
                  </Link>
                </li>
              ))}
            </ol>
          </nav>

          <article className="cz-legal__body">
            {intro && <div className="cz-legal__intro">{intro}</div>}

            {sections.map(({ id, title: sectionTitle, content }, i) => (
              <section key={id} id={id} className="cz-legal__section" aria-labelledby={`${id}-title`}>
                <h2 className="cz-legal__h2" id={`${id}-title`}>
                  <span className="cz-legal__num" aria-hidden="true">{pad(i)}</span>
                  {stripNumber(sectionTitle)}
                </h2>
                <div className="cz-legal__text">{content}</div>
              </section>
            ))}

            {updated && (
              <p className="cz-legal__updated">
                <CalendarDays size={15} strokeWidth={2} aria-hidden="true" />
                {updated}
              </p>
            )}
          </article>
        </div>
      </main>
      <ChatWidget />
    </div>
  );
}
